import { useEffect, useRef, useState, useCallback } from "react";
import api, { fmtSeconds, fmtClockTime } from "@/lib/api";
import { fmtMoney, billable } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Play, Square, Camera, Activity, Pause, AlertTriangle, CheckCircle2, Phone, PhoneOff, LogIn, LogOut
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const HEARTBEAT_INTERVAL_MS = 5000;

export default function EmployeeTracker() {
  const { user, refresh } = useAuth();
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [running, setRunning] = useState(false);
  const [active, setActive] = useState(true); // productivity status
  const [callMode, setCallMode] = useState(false); // "On call" override — counts as productive without input
  const [permissionAsked, setPermissionAsked] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [screenShareError, setScreenShareError] = useState(null);

  // Local counters between heartbeats (FLOAT seconds, drift-safe)
  const lastActivityRef = useRef(Date.now());
  const lastEngagedAtRef = useRef(0); // updated only on key/click — used to classify ticks as engaged vs light
  const lastTickAtRef = useRef(Date.now());
  const productiveAccumRef = useRef(0);
  const idleAccumRef = useRef(0);
  const lightAccumRef = useRef(0); // subset of productive: only mouse moves, no real input (suspicious)
  const callAccumRef = useRef(0); // subset of productive: on a call (no-input grace allowed)
  const callModeRef = useRef(false); // mirror state to refs so tick interval reads latest value
  const keystrokesRef = useRef(0);
  const clicksRef = useRef(0);
  const movesRef = useRef(0);
  const scrollsRef = useRef(0);
  const tickIntervalRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const screenshotIntervalRef = useRef(null);
  const mediaStreamRef = useRef(null);
  // tick used purely to force a re-render every second so the timer visibly advances
  const [, setRenderTick] = useState(0);

  const inactivityMs = (user?.inactivity_timeout_sec || 30) * 1000;
  const screenshotIntervalSec = user?.screenshot_interval_sec || 60;
  const autoStopIdleMs = (user?.auto_stop_idle_min || 0) * 60 * 1000;
  const ENGAGED_WINDOW_MS = 5000; // had a key or click in last 5s → engaged; otherwise → light
  const handleStopRef = useRef(null);

  const loadToday = useCallback(async () => {
    try {
      const { data } = await api.get("/tracker/today");
      setSession(data);
      setRunning(!!data.is_running);
    } catch (e) {
      // ignore
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/tracker/history", { params: { days: 14 } });
      setHistory(data);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadToday();
    loadHistory();
  }, [loadToday, loadHistory]);

  // Activity listeners — count specific input types so we can classify "engaged" vs "light" (mouse-only)
  useEffect(() => {
    const now = () => Date.now();
    const onMove = () => {
      lastActivityRef.current = now();
      movesRef.current += 1;
    };
    const onKey = () => {
      lastActivityRef.current = now();
      lastEngagedAtRef.current = now();
      keystrokesRef.current += 1;
    };
    const onClick = () => {
      lastActivityRef.current = now();
      lastEngagedAtRef.current = now();
      clicksRef.current += 1;
    };
    const onScroll = () => {
      lastActivityRef.current = now();
      scrollsRef.current += 1;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Local tick: wall-clock based (drift- and tab-throttle resilient)
  useEffect(() => {
    if (!running) {
      setActive(true);
      return;
    }
    lastTickAtRef.current = Date.now();
    tickIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const dt = Math.max(0, (now - lastTickAtRef.current) / 1000);
      lastTickAtRef.current = now;
      const idleFor = now - lastActivityRef.current;
      const onCall = callModeRef.current;
      if (onCall) {
        // Call Mode: count as productive + engaged regardless of input.
        // Skip light classification and auto-stop while on a call.
        productiveAccumRef.current += dt;
        callAccumRef.current += dt;
        // Pretend there's "real" engagement so this isn't logged as light
        lastEngagedAtRef.current = now;
        setActive(true);
        setRenderTick((t) => (t + 1) % 1_000_000);
        return;
      }
      if (idleFor < inactivityMs) {
        productiveAccumRef.current += dt;
        // Classify: if no real input (key/click) in the last ENGAGED_WINDOW_MS, this tick is "light"
        // (only mouse moves/scrolls kept the user 'active' — suspicious fake-activity pattern)
        if (now - lastEngagedAtRef.current > ENGAGED_WINDOW_MS) {
          lightAccumRef.current += dt;
        }
        setActive(true);
      } else {
        idleAccumRef.current += dt;
        setActive(false);
      }
      // Auto-stop: stop the tracker entirely if idle exceeds admin-set threshold
      if (autoStopIdleMs > 0 && idleFor >= autoStopIdleMs && handleStopRef.current) {
        toast.warning(`Auto-stopped after ${user?.auto_stop_idle_min} min of inactivity`);
        handleStopRef.current();
        return;
      }
      // force re-render so the display updates every tick (even when active doesn't flip)
      setRenderTick((t) => (t + 1) % 1_000_000);
    }, 1000);
    return () => clearInterval(tickIntervalRef.current);
  }, [running, inactivityMs, autoStopIdleMs, user?.auto_stop_idle_min]);

  // Heartbeat: send accumulated counters to backend every 5s (integer seconds only)
  // Important: we only decrement the local accumulator AFTER a successful API call,
  // so the displayed timer never dips during the round-trip.
  useEffect(() => {
    if (!running) return;
    heartbeatIntervalRef.current = setInterval(async () => {
      const sendP = Math.min(120, Math.floor(productiveAccumRef.current));
      const sendI = Math.min(600, Math.floor(idleAccumRef.current));
      const sendL = Math.min(sendP, Math.floor(lightAccumRef.current));
      const sendCall = Math.min(sendP, Math.floor(callAccumRef.current));
      const sendK = keystrokesRef.current;
      const sendC = clicksRef.current;
      const sendM = movesRef.current;
      const sendS = scrollsRef.current;
      if (sendP === 0 && sendI === 0 && sendK === 0 && sendC === 0 && sendM === 0 && sendS === 0) return;
      try {
        const { data } = await api.post("/tracker/heartbeat", {
          productive_delta_sec: sendP,
          idle_delta_sec: sendI,
          light_delta_sec: sendL,
          call_delta_sec: sendCall,
          keystrokes: sendK,
          mouse_clicks: sendC,
          mouse_moves: sendM,
          scrolls: sendS,
        });
        // Only after success: drop the synced portion from the local accumulators.
        productiveAccumRef.current -= sendP;
        idleAccumRef.current -= sendI;
        lightAccumRef.current -= sendL;
        callAccumRef.current -= sendCall;
        keystrokesRef.current -= sendK;
        clicksRef.current -= sendC;
        movesRef.current -= sendM;
        scrollsRef.current -= sendS;
        setSession(data);
      } catch (e) {
        // leave accumulators intact; next heartbeat will retry
      }
    }, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(heartbeatIntervalRef.current);
  }, [running]);

  // Screenshot capture
  const captureScreenshot = useCallback(async () => {
    if (!mediaStreamRef.current || !user?.screenshot_enabled) return;
    try {
      const stream = mediaStreamRef.current;
      const track = stream.getVideoTracks()[0];
      if (!track || track.readyState !== "live") {
        setScreenSharing(false);
        return;
      }

      // Robust approach: video element + canvas (works in Chrome, Firefox, Edge, Safari desktop)
      const video = document.createElement("video");
      video.muted = true;
      video.playsInline = true;
      video.srcObject = stream;
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
        setTimeout(reject, 3000); // timeout
      });
      await video.play();
      // small delay so the frame is ready
      await new Promise((r) => setTimeout(r, 150));

      const canvas = document.createElement("canvas");
      const maxW = 1280;
      const scale = Math.min(1, maxW / (video.videoWidth || maxW));
      canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
      canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.55));

      video.pause();
      video.srcObject = null;

      if (!blob) return;
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          await api.post("/tracker/screenshot", { image_base64: reader.result });
        } catch (err) {
          console.warn("Screenshot upload failed:", err?.response?.data?.detail || err.message);
        }
      };
      reader.readAsDataURL(blob);
    } catch (e) {
      console.warn("Screenshot capture failed:", e);
    }
  }, [user?.screenshot_enabled]);

  // Screenshot interval
  useEffect(() => {
    if (!running || !user?.screenshot_enabled || !screenSharing) return;
    screenshotIntervalRef.current = setInterval(captureScreenshot, screenshotIntervalSec * 1000);
    return () => clearInterval(screenshotIntervalRef.current);
  }, [running, user?.screenshot_enabled, screenshotIntervalSec, captureScreenshot, screenSharing]);

  const requestScreenShare = async () => {
    if (!user?.screenshot_enabled) return true;
    setScreenShareError(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error("Screen capture is not supported on this browser. Use desktop Chrome, Edge, Firefox, or Safari.");
      }
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          displaySurface: "monitor", // prefer full screen
          cursor: "always",
          frameRate: 5,
        },
        audio: false,
      });
      mediaStreamRef.current = stream;
      setScreenSharing(true);
      stream.getVideoTracks()[0].addEventListener("ended", () => {
        mediaStreamRef.current = null;
        setScreenSharing(false);
        toast.warning("Screen sharing stopped — screenshots paused");
      });
      setPermissionAsked(true);
      toast.success("Screen share active — first snapshot in a moment");
      return true;
    } catch (e) {
      const msg = e?.message || "Permission denied";
      setScreenShareError(msg);
      setScreenSharing(false);
      toast.error(`Screen share unavailable: ${msg}`);
      setPermissionAsked(true);
      return false;
    }
  };

  const handleStart = async () => {
    if (user && !user.tracker_enabled) {
      toast.error("Your tracker is disabled by admin");
      return;
    }

    // Browsers REQUIRE a user gesture for getDisplayMedia — call it FIRST,
    // before any await, so the click handler is still active.
    let shared = false;
    if (user?.screenshot_enabled && !mediaStreamRef.current) {
      shared = await requestScreenShare();
    } else if (mediaStreamRef.current) {
      shared = true;
    }

    try {
      const { data } = await api.post("/tracker/start");
      setSession(data);
      setRunning(true);
      lastActivityRef.current = Date.now();
      toast.success("Tracker started");
      // Take an immediate screenshot so the user sees proof it works (no 60s wait)
      if (shared && user?.screenshot_enabled) {
        setTimeout(() => { captureScreenshot(); }, 800);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start");
    }
  };

  const handleStop = async () => {
    // 1) Always stop locally first — UI must respond even if network fails
    setRunning(false);
    setCallMode(false);
    callModeRef.current = false;
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
    }
    setScreenSharing(false);

    // 2) Try to flush remaining seconds (best-effort, integer-only)
    const sendP = Math.min(120, Math.floor(productiveAccumRef.current));
    const sendI = Math.min(600, Math.floor(idleAccumRef.current));
    const sendL = Math.min(sendP, Math.floor(lightAccumRef.current));
    const sendCall = Math.min(sendP, Math.floor(callAccumRef.current));
    const sendK = keystrokesRef.current;
    const sendC = clicksRef.current;
    const sendM = movesRef.current;
    const sendS = scrollsRef.current;
    if (sendP > 0 || sendI > 0 || sendK > 0 || sendC > 0 || sendM > 0 || sendS > 0) {
      try {
        await api.post("/tracker/heartbeat", {
          productive_delta_sec: sendP,
          idle_delta_sec: sendI,
          light_delta_sec: sendL,
          call_delta_sec: sendCall,
          keystrokes: sendK,
          mouse_clicks: sendC,
          mouse_moves: sendM,
          scrolls: sendS,
        });
        productiveAccumRef.current -= sendP;
        idleAccumRef.current -= sendI;
        lightAccumRef.current -= sendL;
        callAccumRef.current -= sendCall;
        keystrokesRef.current -= sendK;
        clicksRef.current -= sendC;
        movesRef.current -= sendM;
        scrollsRef.current -= sendS;
      } catch (e) { /* ignore — server already has most of it */ }
    }

    // 3) Tell the backend the tracker is stopped
    try {
      const { data } = await api.post("/tracker/stop");
      setSession(data);
    } catch (e) { /* even if this fails, tracker is locally stopped */ }

    toast.success("Tracker stopped");
    loadHistory();
  };

  // Keep a ref so the tick interval can call handleStop without re-binding
  useEffect(() => { handleStopRef.current = handleStop; });

  // Mirror callMode state → ref (so the 1s tick interval always reads the latest value)
  useEffect(() => { callModeRef.current = callMode; }, [callMode]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const productive = (session?.productive_seconds || 0) + (running ? productiveAccumRef.current : 0);
  const idle = (session?.idle_seconds || 0) + (running ? idleAccumRef.current : 0);
  const light = (session?.light_seconds || 0) + (running ? lightAccumRef.current : 0);
  const call = (session?.call_seconds || 0) + (running ? callAccumRef.current : 0);
  const engaged = Math.max(0, productive - light);
  const total = productive + idle;
  const ratio = total > 0 ? Math.round((productive / total) * 100) : 0;
  const engagedRatio = productive > 0 ? Math.round((engaged / productive) * 100) : 0;
  const lightRatio = productive > 0 ? Math.round((light / productive) * 100) : 0;
  const adj = session?.adjustments_seconds || 0;
  const inputs = {
    keystrokes: (session?.keystrokes || 0) + keystrokesRef.current,
    clicks: (session?.mouse_clicks || 0) + clicksRef.current,
    moves: (session?.mouse_moves || 0) + movesRef.current,
    scrolls: (session?.scrolls || 0) + scrollsRef.current,
  };

  const toggleCallMode = () => {
    if (!running) {
      toast.error("Start the tracker first, then enable Call Mode");
      return;
    }
    setCallMode((prev) => {
      const next = !prev;
      // Reset activity timestamps so the moment we exit Call Mode, the regular logic resumes cleanly
      lastActivityRef.current = Date.now();
      lastEngagedAtRef.current = Date.now();
      toast.success(next ? "Call Mode ON — counting as productive without input" : "Call Mode OFF");
      return next;
    });
  };

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">
              [ Tracker / Live ]
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
              Hello, {user?.name}
            </h1>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold">
            <span className={`pulse-dot inline-block h-2 w-2 ${running ? (callMode ? "bg-[#d4a82a]" : active ? "bg-[#1f4d2e]" : "bg-[#d4a82a]") : "bg-neutral-300"}`} />
            {running ? (callMode ? "On Call" : active ? "Active" : "Idle") : "Stopped"}
          </div>
        </div>

        {!user?.tracker_enabled && (
          <div className="mb-6 border border-[#a8421f] bg-[#a8421f]/5 p-4 flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-[#a8421f]" />
            <div className="text-sm">
              <span className="font-bold">Tracker disabled.</span> Contact your admin to enable time tracking.
            </div>
          </div>
        )}

        {/* Big timer card */}
        <Card data-testid="tracker-card" className="rounded-md border border-stone-200 shadow-none mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-neutral-200">
            <div className="lg:col-span-2 p-8">
              <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-2">
                Productive Time / Today
              </div>
              <div data-testid="tracker-productive-time" className="font-mono-num text-7xl sm:text-8xl font-semibold tracking-tighter text-[#1f4d2e] leading-none">
                {fmtSeconds(productive)}
              </div>
              <div className="mt-4 grid grid-cols-3 gap-px bg-stone-200 max-w-md">
                <div className="bg-white p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 font-bold">Idle</div>
                  <div className="font-mono-num text-lg font-bold mt-1">{fmtSeconds(idle)}</div>
                </div>
                <div className="bg-white p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 font-bold">Total</div>
                  <div className="font-mono-num text-lg font-bold mt-1">{fmtSeconds(total)}</div>
                </div>
                <div className="bg-white p-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-stone-500 font-bold">Adj.</div>
                  <div className={`font-mono-num text-lg font-bold mt-1 ${adj < 0 ? "text-[#a8421f]" : adj > 0 ? "text-[#1f4d2e]" : ""}`}>
                    {adj >= 0 ? "+" : ""}{fmtSeconds(Math.abs(adj))}
                  </div>
                </div>
              </div>

              {user?.hourly_rate > 0 && (
                <div className="mt-4 inline-flex items-center gap-3 px-4 py-3 rounded-md border border-[#d4a82a]/40 bg-[#d4a82a]/10">
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#7a5e0f]">Billable today</div>
                    <div className="font-mono-num text-2xl font-bold text-[#1f4d2e]">
                      {fmtMoney(billable(productive, user.hourly_rate), user.currency)}
                    </div>
                  </div>
                  <div className="h-10 w-px bg-[#d4a82a]/40" />
                  <div className="text-[11px] text-stone-600">
                    <div className="font-mono-num">{fmtMoney(user.hourly_rate, user.currency)}/h</div>
                    <div>{(productive / 3600).toFixed(2)} productive hrs</div>
                  </div>
                </div>
              )}
              <div className="mt-6 flex items-center gap-3 text-xs tracking-[0.15em] uppercase font-bold text-stone-500">
                <span>Productive Ratio:</span>
                <span className="font-mono-num text-[#1f4d2e] text-base">{ratio}%</span>
                <div className="flex-1 h-2 bg-stone-100 max-w-[200px]">
                  <div className="h-full bg-[#1f4d2e] transition-all" style={{ width: `${ratio}%` }} />
                </div>
              </div>
            </div>

            <div className="p-8 flex flex-col justify-between bg-stone-50">
              <div>
                <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-3">
                  Controls
                </div>
                {!running ? (
                  <Button
                    data-testid="tracker-start-button"
                    onClick={handleStart}
                    disabled={!user?.tracker_enabled}
                    className="w-full h-14 rounded-md bg-[#1f4d2e] hover:bg-[#0f3a1f] text-stone-100 text-[15px] font-medium tracking-wide flex items-center justify-center gap-3 transition-elegant"
                  >
                    <Play className="h-4 w-4" fill="currentColor" /> Start tracker
                  </Button>
                ) : (
                  <Button
                    data-testid="tracker-stop-button"
                    onClick={handleStop}
                    className="w-full h-14 rounded-md bg-[#a8421f] hover:bg-[#7c2f15] text-stone-100 text-[15px] font-medium tracking-wide flex items-center justify-center gap-3 transition-elegant"
                  >
                    <Square className="h-4 w-4" fill="currentColor" /> Stop tracker
                  </Button>
                )}

                {/* Call Mode toggle — only visible if super_admin or business client granted permission */}
                {user?.call_mode_enabled && (
                  <>
                    <Button
                      data-testid="tracker-call-mode-button"
                      onClick={toggleCallMode}
                      disabled={!running}
                      className={`w-full h-12 mt-2 rounded-md text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-elegant ${
                        callMode
                          ? "bg-[#d4a82a] hover:bg-[#b8902a] text-[#1f4d2e] ring-2 ring-[#d4a82a] ring-offset-2"
                          : "bg-white hover:bg-stone-50 text-[#1f4d2e] border border-stone-300"
                      }`}
                    >
                      {callMode ? (
                        <>
                          <span className="h-2 w-2 rounded-full bg-[#1f4d2e] pulse-dot" />
                          <Phone className="h-4 w-4" /> ON CALL — End call
                        </>
                      ) : (
                        <>
                          <Phone className="h-4 w-4" /> Call Mode (cold-call)
                        </>
                      )}
                    </Button>
                    {callMode && (
                      <div className="mt-2 text-[11px] text-[#7a5e0f] leading-relaxed">
                        Tracker is counting as <span className="font-bold">productive</span> without mouse/keyboard input.
                        Tap again when the call ends.
                      </div>
                    )}
                  </>
                )}
                <div className="mt-4 space-y-2 text-xs">
                  <div className="flex justify-between">
                    <span className="text-stone-500 tracking-wider uppercase">Inactivity Pause</span>
                    <span className="font-mono-num font-bold">{user?.inactivity_timeout_sec}s</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500 tracking-wider uppercase">Auto-Stop Idle</span>
                    <span className="font-mono-num font-bold">
                      {user?.auto_stop_idle_min > 0 ? `${user.auto_stop_idle_min}m` : "OFF"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-stone-500 tracking-wider uppercase">Screenshot Every</span>
                    <span className="font-mono-num font-bold">
                      {user?.screenshot_enabled ? `${user?.screenshot_interval_sec}s` : "OFF"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-stone-200 text-[11px] leading-relaxed">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Camera className="h-3.5 w-3.5 text-stone-600" />
                    <span className="font-bold tracking-wider uppercase text-stone-600">Screen Share</span>
                  </div>
                  {user?.screenshot_enabled ? (
                    screenSharing ? (
                      <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-semibold text-[#1f4d2e]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#1f4d2e] pulse-dot" /> Active
                      </span>
                    ) : screenShareError ? (
                      <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#a8421f]">
                        Denied
                      </span>
                    ) : (
                      <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-stone-400">
                        Not started
                      </span>
                    )
                  ) : (
                    <span className="text-[10px] tracking-[0.15em] uppercase font-semibold text-stone-400">
                      Off (admin)
                    </span>
                  )}
                </div>
                <p className="text-stone-500">
                  {user?.screenshot_enabled
                    ? screenSharing
                      ? "Screenshots are being captured. To stop, click Stop tracker or end the browser share."
                      : 'When you click "Start tracker", your browser will ask to share your screen. Choose "Entire screen" and click Share.'
                    : "Screenshots disabled by admin."}
                </p>
                {screenShareError && (
                  <p className="mt-2 text-[#a8421f] text-[11px]">{screenShareError}</p>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Hours breakdown — Pie chart */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white border border-stone-200 rounded-md p-6">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="font-display text-2xl font-medium text-[#1f4d2e]">Today's hours</h3>
              <div className="font-mono-num text-sm text-stone-500">
                {(total / 3600).toFixed(2)} h
              </div>
            </div>
            <div className="font-display-italic text-sm text-stone-500 mb-4">
              Productive vs idle — at a glance
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Productive", value: productive },
                      { name: "Idle", value: idle },
                    ]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={56}
                    outerRadius={92}
                    paddingAngle={2}
                    stroke="#fdfcf7"
                    strokeWidth={3}
                  >
                    <Cell fill="#1f4d2e" />
                    <Cell fill="#d4a82a" />
                  </Pie>
                  <Tooltip
                    formatter={(v) => fmtSeconds(v)}
                    contentStyle={{ borderRadius: 6, border: "1px solid #e7e5e4", fontFamily: "Inter", fontSize: 12 }}
                  />
                  <Legend
                    iconType="square"
                    wrapperStyle={{ fontFamily: "Inter", fontSize: 12, paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 text-center">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Productive</div>
                <div className="font-mono-num text-xl font-semibold text-[#1f4d2e]">{(productive / 3600).toFixed(2)}h</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Idle</div>
                <div className="font-mono-num text-xl font-semibold text-[#d4a82a]">{(idle / 3600).toFixed(2)}h</div>
              </div>
            </div>
          </div>

          {/* Weekly pie — last 7 days */}
          <div className="bg-white border border-stone-200 rounded-md p-6">
            <div className="flex items-baseline justify-between mb-1">
              <h3 className="font-display text-2xl font-medium text-[#1f4d2e]">Last 7 days</h3>
              <div className="font-mono-num text-sm text-stone-500">
                {((history.reduce((a, b) => a + (b.total_seconds || 0), 0)) / 3600).toFixed(1)} h
              </div>
            </div>
            <div className="font-display-italic text-sm text-stone-500 mb-4">
              Where your week has gone
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={[
                      { name: "Productive", value: history.slice(0, 7).reduce((a, b) => a + (b.productive_seconds || 0), 0) },
                      { name: "Idle", value: history.slice(0, 7).reduce((a, b) => a + (b.idle_seconds || 0), 0) },
                    ]}
                    dataKey="value"
                    cx="50%"
                    cy="50%"
                    innerRadius={56}
                    outerRadius={92}
                    paddingAngle={2}
                    stroke="#fdfcf7"
                    strokeWidth={3}
                  >
                    <Cell fill="#1f4d2e" />
                    <Cell fill="#d4a82a" />
                  </Pie>
                  <Tooltip
                    formatter={(v) => fmtSeconds(v)}
                    contentStyle={{ borderRadius: 6, border: "1px solid #e7e5e4", fontFamily: "Inter", fontSize: 12 }}
                  />
                  <Legend
                    iconType="square"
                    wrapperStyle={{ fontFamily: "Inter", fontSize: 12, paddingTop: 8 }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 pt-4 border-t border-stone-100 grid grid-cols-2 text-center">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Productive</div>
                <div className="font-mono-num text-xl font-semibold text-[#1f4d2e]">
                  {(history.slice(0, 7).reduce((a, b) => a + (b.productive_seconds || 0), 0) / 3600).toFixed(1)}h
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 mb-1">Idle</div>
                <div className="font-mono-num text-xl font-semibold text-[#d4a82a]">
                  {(history.slice(0, 7).reduce((a, b) => a + (b.idle_seconds || 0), 0) / 3600).toFixed(1)}h
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Activity status row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 mb-6 border border-stone-200">
          <div className="bg-white p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-bold mb-2 flex items-center gap-2">
              <Activity className="h-3 w-3" /> Status
            </div>
            <div className="flex items-center gap-2">
              {running ? (
                callMode ? (
                  <Badge className="rounded-md bg-[#d4a82a] text-[#1f4d2e]"><Phone className="h-3 w-3 mr-1" />ON CALL</Badge>
                ) : active ? (
                  <Badge className="rounded-md bg-[#1f4d2e] text-white"><CheckCircle2 className="h-3 w-3 mr-1" />ACTIVE</Badge>
                ) : (
                  <Badge className="rounded-md bg-[#d4a82a] text-black"><Pause className="h-3 w-3 mr-1" />IDLE — PAUSED</Badge>
                )
              ) : (
                <Badge variant="secondary" className="rounded-md">STOPPED</Badge>
              )}
            </div>
          </div>
          <div className="bg-white p-5" data-testid="time-in-card">
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-bold mb-2 flex items-center gap-2">
              <LogIn className="h-3 w-3" /> Time In
            </div>
            <div className="font-mono-num font-bold text-lg text-[#1f4d2e]">
              {fmtClockTime(session?.first_start_at)}
            </div>
            <div className="text-[10px] text-stone-400 mt-0.5 font-display-italic">
              {session?.first_start_at ? "first start today" : "not started yet"}
            </div>
          </div>
          <div className="bg-white p-5" data-testid="time-out-card">
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-bold mb-2 flex items-center gap-2">
              <LogOut className="h-3 w-3" /> Time Out
            </div>
            <div className="font-mono-num font-bold text-lg text-[#a8421f]">
              {running ? "—" : fmtClockTime(session?.last_stop_at)}
            </div>
            <div className="text-[10px] text-stone-400 mt-0.5 font-display-italic">
              {running ? "still running" : session?.last_stop_at ? "last stop today" : "—"}
            </div>
          </div>
          <div className="bg-white p-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-bold mb-2">Last Heartbeat</div>
            <div className="font-mono-num text-sm">{session?.last_heartbeat_at ? new Date(session.last_heartbeat_at).toLocaleTimeString() : "—"}</div>
          </div>
        </div>

        {/* Activity Quality — engaged vs light (suspicious) vs idle */}
        <div className="border border-stone-200 bg-white rounded-md p-6 mb-6" data-testid="activity-quality-card">
          <div className="flex items-baseline justify-between flex-wrap gap-3 mb-1">
            <h3 className="font-display text-2xl font-medium text-[#1f4d2e]">Activity Quality</h3>
            <div className="flex items-center gap-3 text-xs tracking-[0.22em] uppercase font-bold text-stone-500">
              {call > 0 && (
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-[#d4a82a]/15 border border-[#d4a82a]/40 text-[#7a5e0f]">
                  <Phone className="h-3 w-3" /> Call {fmtSeconds(call)}
                </span>
              )}
              <span>Today · live</span>
            </div>
          </div>
          <p className="font-display-italic text-sm text-stone-500 mb-5">
            Engaged time (real input) vs light activity (only mouse movement — common "fake activity" pattern) vs idle.
          </p>

          {/* Stacked bar */}
          <div className="h-3 w-full bg-stone-100 rounded-full overflow-hidden flex" title="Engaged · Light · Idle">
            {total > 0 ? (
              <>
                <div className="h-full bg-[#1f4d2e]" style={{ width: `${(engaged / total) * 100}%` }} title={`Engaged: ${(engaged/3600).toFixed(2)}h`} />
                <div className="h-full bg-[#d4a82a]" style={{ width: `${(light / total) * 100}%` }} title={`Light: ${(light/3600).toFixed(2)}h`} />
                <div className="h-full bg-stone-300" style={{ width: `${(idle / total) * 100}%` }} title={`Idle: ${(idle/3600).toFixed(2)}h`} />
              </>
            ) : (
              <div className="h-full bg-stone-200 w-full" />
            )}
          </div>

          <div className="grid grid-cols-3 gap-px bg-stone-200 border border-stone-200 mt-4">
            <div className="bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-sm bg-[#1f4d2e]" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-600">Engaged</span>
              </div>
              <div className="font-mono-num text-3xl font-semibold text-[#1f4d2e]">{fmtSeconds(engaged)}</div>
              <div className="text-xs text-stone-500 mt-0.5 font-mono-num">{engagedRatio}% of productive</div>
            </div>
            <div className="bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-sm bg-[#d4a82a]" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-600">Light <span className="text-stone-400">(no clicks/keys)</span></span>
              </div>
              <div className={`font-mono-num text-3xl font-semibold ${lightRatio > 30 ? "text-[#a8421f]" : "text-[#7a5e0f]"}`}>{fmtSeconds(light)}</div>
              <div className="text-xs text-stone-500 mt-0.5 font-mono-num">{lightRatio}% of productive</div>
            </div>
            <div className="bg-white p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="h-2 w-2 rounded-sm bg-stone-400" />
                <span className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-600">Idle</span>
              </div>
              <div className="font-mono-num text-3xl font-semibold text-stone-500">{fmtSeconds(idle)}</div>
              <div className="text-xs text-stone-500 mt-0.5 font-mono-num">paused</div>
            </div>
          </div>

          {/* Per-input counters */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-stone-200 border border-stone-200 mt-4">
            {[
              { l: "Keystrokes", v: inputs.keystrokes },
              { l: "Clicks", v: inputs.clicks },
              { l: "Mouse moves", v: inputs.moves },
              { l: "Scrolls", v: inputs.scrolls },
            ].map((k) => (
              <div key={k.l} className="bg-white p-3">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-stone-500">{k.l}</div>
                <div className="font-mono-num text-xl font-semibold mt-1">{k.v.toLocaleString()}</div>
              </div>
            ))}
          </div>

          {lightRatio > 30 && light > 60 && (
            <div className="mt-4 p-3 border border-[#a8421f]/40 bg-[#a8421f]/5 rounded-md text-sm text-[#a8421f] flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <span className="font-bold">Heads up:</span> over {lightRatio}% of your productive time is mouse-movement-only.
                Real work usually has clicks &amp; typing. Your admin can see this same breakdown.
              </div>
            </div>
          )}
        </div>

        {/* History preview */}
        <div className="border border-stone-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-stone-50">
            <div className="text-xs tracking-[0.25em] uppercase font-bold">Recent Work History</div>
            <a href="/history" className="text-xs text-[#1f4d2e] font-bold tracking-wider uppercase hover:underline">View all →</a>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-white border-b border-stone-200 text-[10px] tracking-[0.2em] uppercase text-stone-500">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Date</th>
                <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogIn className="h-3 w-3" />Time In</span></th>
                <th className="text-left px-5 py-3 font-bold"><span className="inline-flex items-center gap-1.5"><LogOut className="h-3 w-3" />Time Out</span></th>
                <th className="text-right px-5 py-3 font-bold">Productive</th>
                <th className="text-right px-5 py-3 font-bold">Idle</th>
                <th className="text-right px-5 py-3 font-bold">Total</th>
              </tr>
            </thead>
            <tbody>
              {history.slice(0, 7).map((h) => (
                <tr key={h.id} className="border-b border-stone-100 hover:bg-stone-50">
                  <td className="px-5 py-3 font-mono-num">{h.date}</td>
                  <td className="px-5 py-3 font-mono-num text-[#1f4d2e]">{fmtClockTime(h.first_start_at)}</td>
                  <td className="px-5 py-3 font-mono-num text-[#a8421f]">
                    {h.is_running ? <span className="text-stone-400">running…</span> : fmtClockTime(h.last_stop_at)}
                  </td>
                  <td className="px-5 py-3 text-right font-mono-num text-[#1f4d2e] font-bold">{fmtSeconds(h.productive_seconds)}</td>
                  <td className="px-5 py-3 text-right font-mono-num text-stone-500">{fmtSeconds(h.idle_seconds)}</td>
                  <td className="px-5 py-3 text-right font-mono-num font-bold">{fmtSeconds(h.total_seconds)}</td>
                </tr>
              ))}
              {history.length === 0 && (
                <tr><td colSpan="6" className="text-center py-10 text-sm text-stone-500">No history yet — start tracking to build it.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
